import {
  FILTER_FIELDS_TO_OPERATION,
  FILTER_MAPPING,
  FILTERS,
  FIVE_AM_IN_MIN,
  JOB_ORDER_TIME_STATUSES,
  NINE_THIRTY_PM_IN_MIN,
  ONE_THIRTY_PM_IN_MIN,
  SEARCH_PARAM_KEYS,
  SHIFT_OPTION_KEYS,
} from '../constants/jobOrderConstants';

import { getTimeZone, isShiftCompleted, isShiftStarted } from './miscHelpers';
import { epochToDateInReadableFormat, epochToDayOfWeek } from './timeConverter';

export const groupJobOrdersByStartDate = (jobOrders) =>
  jobOrders.reduce((acc, curr) => {
    const weekDay = epochToDayOfWeek(curr.start);
    const date = epochToDateInReadableFormat(curr.start);
    if (acc[date]) {
      acc[date].cards.push(curr);
    } else {
      acc[date] = { weekDay, cards: [curr] };
    }
    return acc;
  }, {});

export const getTimeStatus = (start, end) => {
  if (!isShiftStarted(start)) {
    return JOB_ORDER_TIME_STATUSES.UPCOMING;
  }
  if (isShiftCompleted(end)) {
    return JOB_ORDER_TIME_STATUSES.COMPLETED;
  }
  return JOB_ORDER_TIME_STATUSES.ACTIVE;
};

/**
 * Deduplicates an array of objects by a unique key.
 * @param {Array} array - Array of objects to deduplicate
 * @param {Function} [keyExtractor] - Optional function to extract the key from each item.
 *   For React elements, pass a keyExtractor since element.key may not be accessible.
 *   Default tries: props.placement.id, props.placement.employeeId, item.id, item.key
 */
export const getUniqObjectsByKey = (
  array,
  keyExtractor = (item, index) =>
    item?.props?.placement?.id ??
    item?.props?.placement?.employeeId ??
    item?.id ??
    item?.key ??
    index
) => [
  ...new Map(
    array.map((item, index) => {
      const key = keyExtractor(item, index);
      const hasValidKey = key !== null && key !== undefined && key !== '';
      return [hasValidKey ? key : index, item];
    })
  ).values(),
];

export const getNewShiftFiltersCore = (filters) => {
  if (!Array.isArray(filters)) {
    return [];
  }
  if (filters.length === 1) {
    return filters;
  }
  const first = filters[0];
  const second = filters[1];
  if (
    filters.length === 2 &&
    first?.value != null &&
    second?.value != null &&
    typeof first.value.start === 'number' &&
    typeof second.value.start === 'number'
  ) {
    const startSum = first.value.start + second.value.start;

    const getValue = () => {
      if (startSum === FIVE_AM_IN_MIN + ONE_THIRTY_PM_IN_MIN) {
        return {
          start: FIVE_AM_IN_MIN,
          end: NINE_THIRTY_PM_IN_MIN,
        };
      }
      if (startSum === FIVE_AM_IN_MIN + NINE_THIRTY_PM_IN_MIN) {
        return {
          start: NINE_THIRTY_PM_IN_MIN,
          end: ONE_THIRTY_PM_IN_MIN,
        };
      }
      if (startSum === NINE_THIRTY_PM_IN_MIN + ONE_THIRTY_PM_IN_MIN) {
        return {
          start: ONE_THIRTY_PM_IN_MIN,
          end: FIVE_AM_IN_MIN,
        };
      }
      return {};
    };

    return [
      {
        field: first.field,
        operation: first.operation,
        value: { ...getValue(), zone: first.value.zone },
      },
    ];
  }
  return [];
};

export const getNewShiftFilters = (filters) => getNewShiftFiltersCore(filters);

const { FIVE_AM_TO_ONE_THIRTY_PM, ONE_THIRTY_PM_TO_NINE_THIRTY_PM, NINE_THIRTY_PM_TO_FIVE_AM } =
  SHIFT_OPTION_KEYS;

const shiftTimeCombinations = {
  [`${FIVE_AM_IN_MIN}-${ONE_THIRTY_PM_IN_MIN}`]: [FIVE_AM_TO_ONE_THIRTY_PM],
  [`${ONE_THIRTY_PM_IN_MIN}-${NINE_THIRTY_PM_IN_MIN}`]: [ONE_THIRTY_PM_TO_NINE_THIRTY_PM],
  [`${NINE_THIRTY_PM_IN_MIN}-${FIVE_AM_IN_MIN}`]: [NINE_THIRTY_PM_TO_FIVE_AM],
  [`${FIVE_AM_IN_MIN}-${NINE_THIRTY_PM_IN_MIN}`]: [
    FIVE_AM_TO_ONE_THIRTY_PM,
    ONE_THIRTY_PM_TO_NINE_THIRTY_PM,
  ],
  [`${NINE_THIRTY_PM_IN_MIN}-${ONE_THIRTY_PM_IN_MIN}`]: [
    FIVE_AM_TO_ONE_THIRTY_PM,
    NINE_THIRTY_PM_TO_FIVE_AM,
  ],
  [`${ONE_THIRTY_PM_IN_MIN}-${FIVE_AM_IN_MIN}`]: [
    ONE_THIRTY_PM_TO_NINE_THIRTY_PM,
    NINE_THIRTY_PM_TO_FIVE_AM,
  ],
};

export const getInitialSelectedShiftOptions = (shiftStart, shiftEnd) => {
  const key = `${shiftStart}-${shiftEnd}`;
  return shiftTimeCombinations[key] || [];
};

const buildShiftTimePayload = (filters, start, end) => {
  const removedPrevShifts = filters.filter(
    (f) => f.field !== FILTER_FIELDS_TO_OPERATION.shift.field
  );
  if (!start || !end) {
    return removedPrevShifts;
  }

  const shiftPayload = {
    field: FILTER_FIELDS_TO_OPERATION.shift.field,
    operation: FILTER_FIELDS_TO_OPERATION.shift.operation,
    value: { start: parseInt(start, 10), end: parseInt(end, 10), zone: getTimeZone() },
  };

  return [...removedPrevShifts, shiftPayload];
};

const buildDateRangePayload = (filters, start, end) => {
  const removedPrevDateRange = filters.filter(
    (f) =>
      f.field !== FILTER_FIELDS_TO_OPERATION.startDate.field &&
      f.field !== FILTER_FIELDS_TO_OPERATION.endDate.field
  );

  if (!start || !end) return [...removedPrevDateRange, ...FILTERS.UPCOMING_JOBS];

  const parsedStartDate = parseInt(start, 10);
  const parsedEndDate = parseInt(end, 10);
  const dateRangePayload = [
    {
      value: parsedStartDate,
      field: FILTER_FIELDS_TO_OPERATION.startDate.field,
      operation: FILTER_FIELDS_TO_OPERATION.startDate.operation,
    },
    {
      value: parsedEndDate,
      field: FILTER_FIELDS_TO_OPERATION.endDate.field,
      operation: FILTER_FIELDS_TO_OPERATION.endDate.operation,
    },
  ];

  return [...removedPrevDateRange, ...dateRangePayload];
};

export const buildFilterPayloadFromUrl = (searchParams, prevFilters) => {
  const newFilters = Object.entries(FILTER_MAPPING).reduce(
    (acc, [paramKey, { field, operation, convertValue }]) => {
      const paramValue = searchParams.get(paramKey);
      const value = convertValue ? convertValue(paramValue) : paramValue;
      const updatedFilters = acc.filter((filter) => filter.field !== field);
      return value !== null ? [...updatedFilters, { field, operation, value }] : updatedFilters;
    },
    prevFilters
  );

  const shiftStart = searchParams.get(SEARCH_PARAM_KEYS.SHIFT_START);
  const shiftEnd = searchParams.get(SEARCH_PARAM_KEYS.SHIFT_END);
  const shiftsIncluded = buildShiftTimePayload(newFilters, shiftStart, shiftEnd);

  const startDate = searchParams.get(SEARCH_PARAM_KEYS.START_DATE);
  const endDate = searchParams.get(SEARCH_PARAM_KEYS.END_DATE);

  return buildDateRangePayload(shiftsIncluded, startDate, endDate);
};
